/**
 * BackendStatusContext — globalny detektor niedostępności platformy.
 *
 * Wykrywa padnięty backend WSZYSTKIMI dostępnymi kanałami, żeby ekran przerwy włączał się
 * natychmiast, a nie dopiero po odświeżeniu (F5):
 *   1. Poll /api/health co 15 s (online) / 6 s (offline) — backstop.
 *   2. Interceptor axios → event 'backend-network-error' przy braku odpowiedzi ORAZ przy
 *      bramkowych 502/503/504 (nginx żyje, usługa .NET padła — najczęstszy przypadek prod).
 *   3. Zerwane połączenie SignalR (onclose/onreconnecting) — najszybszy objaw śmierci serwera.
 *   4. Browser online/offline oraz powrót do uśpionej karty (visibilitychange).
 *
 * Każdy sygnał jedynie WYZWALA weryfikację /health — dopiero jej wynik decyduje o trybie offline,
 * więc pojedynczy blip (502, chwilowy reconnect) nie wywala UI, jeśli /health odpowie poprawnie.
 *
 * Gdy backend jest down → cały UI zastępuje ekran przerwy (offlineFallback).
 * Gdy backend wraca → TWARDY reload strony. Świadomie nie zostawiamy zamrożonego, zalogowanego
 *   drzewa Reacta (z danymi kadrowymi/płacowymi na ekranie) — sesja mogła w międzyczasie wygasnąć
 *   lub zostać zrotowana. Reload re-bootstrapuje aplikację i wymusza świeżą walidację sesji.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";
const HEALTH_URL = `${API_URL}/health`;

const POLL_ONLINE = 15_000;    // 15 s gdy backend działa
const POLL_OFFLINE = 6_000;    // 6 s gdy backend nie odpowiada (szybszy powrót do gry)
/* 🔴 [pomiar 2026-09-22] SUFIT NARASTAJĄCEGO ODSTĘPU. Przy zdegradowanej sieci (zmierzone:
   RTT 524 ms i 16% strat pakietów na trasie OVH) sprawdzenie stanu regularnie przepada,
   aplikacja uznaje backend za offline i PRZYSPIESZA odpytywanie do 6 s. To pętla, która sama
   się napędza: więcej żądań na zapchanej drodze to więcej przepadających żądań, a przeglądarka
   ma tylko sześć połączeń na host — zajęte ankietą, głodzą żądania, na które człowiek czeka.
   Przy kolejnych niepowodzeniach odstęp się PODWAJA aż do tego sufitu i wraca do 6 s przy
   pierwszej udanej odpowiedzi. Szybki powrót do gry zostaje; znika tylko dobijanie. */
const POLL_OFFLINE_MAX = 60_000;
const INITIAL_TIMEOUT = 5_000; // 5 s na pierwszy check
const RECOVERY_RELOAD_DELAY = 1_100; // ile pokazać "Połączenie przywrócone" przed reloadem

// Sekundy między próbami w trybie offline — jedno źródło prawdy dla licznika na ekranie przerwy.
export const POLL_OFFLINE_SECONDS = POLL_OFFLINE / 1000;

const BackendStatusContext = createContext({
    isBackendOnline: null,   // null = pierwsze sprawdzenie, true = ok, false = down
    lastChecked: null,
    isVerifying: false,      // trwa sprawdzenie /health (dla animacji na ekranie przerwy)
    justRecovered: false,    // backend wrócił — trwa krótka pauza przed twardym reloadem
    checkNow: () => {},
});

export const useBackendStatus = () => useContext(BackendStatusContext);

export const BackendStatusProvider = ({ children, offlineFallback }) => {
    const [isBackendOnline, setIsBackendOnline] = useState(null);
    const [lastChecked, setLastChecked] = useState(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const [justRecovered, setJustRecovered] = useState(false);

    const timerRef = useRef(null);
    const wasOfflineRef = useRef(false);          // czy byliśmy w trybie offline (do recovery)
    const networkErrorDebounceRef = useRef(null); // debounce lawiny network errorów
    const recoveryCountRef = useRef(0);
    /** Ile sprawdzeń z rzędu nie doszło — steruje narastającym odstępem (patrz POLL_OFFLINE_MAX). */
    const nieudaneZRzeduRef = useRef(0);           // ile z rzędu health OK po awarii
    const reloadScheduledRef = useRef(false);     // strażnik: zaplanuj reload tylko raz

    const checkHealth = useCallback(async (timeout = 5000) => {
        // Nie startuj kolejnego sprawdzenia, jeśli już lecimy z reloadem po recovery.
        if (reloadScheduledRef.current) return true;
        setIsVerifying(true);
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            const response = await fetch(HEALTH_URL, {
                method: "GET",
                signal: controller.signal,
                cache: "no-store",
            });
            clearTimeout(timeoutId);

            if (response.ok) {
                // ── Recovery: backend wrócił po awarii ──
                if (wasOfflineRef.current) {
                    recoveryCountRef.current += 1;

                    // Wymagaj 2 udanych sprawdzeń z rzędu (anti-flap), zanim uznamy recovery.
                    if (recoveryCountRef.current >= 2) {
                        wasOfflineRef.current = false;
                        recoveryCountRef.current = 0;
                        setLastChecked(new Date());

                        // Pokaż moment „Połączenie przywrócone", potem TWARDY reload — świeży
                        // bootstrap + walidacja sesji, zero zastygłego zalogowanego drzewa.
                        if (!reloadScheduledRef.current) {
                            reloadScheduledRef.current = true;
                            setJustRecovered(true);
                            setTimeout(() => { window.location.reload(); }, RECOVERY_RELOAD_DELAY);
                        }
                        return true;
                    }

                    // Pierwsze udane sprawdzenie po awarii — jeszcze nie recovery.
                    setLastChecked(new Date());
                    return true;
                }

                recoveryCountRef.current = 0;
                nieudaneZRzeduRef.current = 0;
                setIsBackendOnline(true);
                setLastChecked(new Date());
                return true;
            }

            // Response nie-OK (502, 503, 500 itp.) = backend nie obsługuje żądań → offline.
            recoveryCountRef.current = 0;
            nieudaneZRzeduRef.current += 1;
            wasOfflineRef.current = true;
            setIsBackendOnline(false);
            setLastChecked(new Date());
            return false;
        } catch {
            // Brak odpowiedzi (connection refused / timeout / DNS) → offline.
            recoveryCountRef.current = 0;
            wasOfflineRef.current = true;
            setIsBackendOnline(false);
            setLastChecked(new Date());
            return false;
        } finally {
            setIsVerifying(false);
        }
    }, []);

    // Pierwsze sprawdzenie przy starcie.
    useEffect(() => {
        checkHealth(INITIAL_TIMEOUT);
    }, [checkHealth]);

    /* Poll — szybszy gdy offline (backstop dla zdarzeń).
       [WYDAJNOSC 2026-09-12] W UKRYTEJ KARCIE NIE PYTAMY. Pomiar produkcji z 7 dni: `/api/health` to
       78 089 wywołań (trzecie miejsce w zużyciu czasu, 2 309 s łącznie), a KAŻDE puka do bazy
       (`CanConnectAsync`). Ludzie trzymają aplikację otwartą w tle przez cały dzień, więc większość tych
       zapytań dotyczyła kart, na które nikt nie patrzył. Powrót do karty i tak wymusza sprawdzenie
       (efekt `visibilitychange` niżej), więc człowiek nie zobaczy różnicy — poza tym, że aplikacja
       odpowiada szybciej. */
    useEffect(() => {
        const interval = isBackendOnline === false
            ? Math.min(POLL_OFFLINE * (2 ** Math.max(0, nieudaneZRzeduRef.current - 1)), POLL_OFFLINE_MAX)
            : POLL_ONLINE;
        timerRef.current = setInterval(() => {
            if (document.visibilityState === "hidden") return;
            checkHealth();
        }, interval);
        return () => clearInterval(timerRef.current);
    }, [isBackendOnline, checkHealth]);

    // Zdarzenia przeglądarki: online/offline + powrót do uśpionej karty.
    useEffect(() => {
        const handleOnline = () => checkHealth();
        // [A15-16] BYŁO ŹLE: zdarzenie przeglądarki 'offline' od razu ustawiało isBackendOnline=false
        //   (plus wasOfflineRef=true, czyli zaplanowany twardy reload przy powrocie) — BEZ jednego
        //   zapytania do /health. Przeglądarka wystawia to zdarzenie także przy mrugnięciu Wi-Fi,
        //   przełączeniu sieci czy wybudzeniu laptopa, a chwilę później sieć już jest.
        //   SKUTEK DLA UŻYTKOWNIKA: sekundowa utrata sieci w trakcie pisania odpowiedzi w zgłoszeniu
        //   albo wypełniania wniosku HR podmieniała CAŁĄ aplikację na ekran przerwy technicznej,
        //   a po powrocie sieci robiła twardy reload — niezapisana treść formularza przepadała,
        //   mimo że backend ani razu nie odmówił odpowiedzi.
        //   NAPRAWA: 'offline' traktujemy tak samo jak sygnał z axios/SignalR (linia niżej) — jako
        //   POWÓD DO WERYFIKACJI, a nie werdykt. Tryb offline zapada dopiero, gdy /health faktycznie
        //   nie odpowie. Szybkość wykrycia REALNEJ awarii zostaje: przy braku sieci fetch odrzuca
        //   się natychmiast (przeglądarka wie, że jest offline), więc ekran przerwy pojawia się
        //   tak samo szybko jak dotąd, a limit czasu jest ten sam co dla sygnału sieciowego.
        // ZMIANA ZACHOWANIA: przy KRÓTKIM mrugnięciu sieci użytkownik nie zobaczy już ekranu przerwy
        //   i nie straci wypełnionego formularza; przy realnej awarii wszystko działa jak dotąd.
        const handleOffline = () => { checkHealth(INITIAL_TIMEOUT); };
        // Timery w tle są throttlowane — karta wróciła na wierzch po dłuższym czasie może pokazywać
        // nieświeży stan. Natychmiast re-weryfikuj, gdy zakładka znów staje się widoczna.
        const handleVisibility = () => {
            if (document.visibilityState === "visible") checkHealth(INITIAL_TIMEOUT);
        };
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        document.addEventListener("visibilitychange", handleVisibility);
        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [checkHealth]);

    // ── Sygnały z axios/SignalR/logowania (natychmiastowa detekcja) ──
    useEffect(() => {
        const handleNetworkError = () => {
            // Debounce — lawinę eventów z jednej awarii ścinamy do jednego sprawdzenia na 2 s.
            if (networkErrorDebounceRef.current) return;
            networkErrorDebounceRef.current = true;
            setTimeout(() => { networkErrorDebounceRef.current = false; }, 2000);

            // NIE przełączamy od razu całego UI w offline (utrata niezapisanych formularzy). Najpierw
            // weryfikacja /health — offline zapadnie tylko, jeśli health faktycznie nie odpowiada.
            checkHealth(INITIAL_TIMEOUT);
        };

        window.addEventListener("backend-network-error", handleNetworkError);
        return () => window.removeEventListener("backend-network-error", handleNetworkError);
    }, [checkHealth]);

    const value = {
        isBackendOnline,
        lastChecked,
        isVerifying,
        justRecovered,
        checkNow: checkHealth,
    };

    // Backend down → cały UI zastępuje ekran przerwy.
    if (isBackendOnline === false && offlineFallback) {
        return (
            <BackendStatusContext.Provider value={value}>
                {offlineFallback}
            </BackendStatusContext.Provider>
        );
    }

    // Wciąż trwa pierwsze sprawdzenie — nic nie renderujemy (loader ogarnia Suspense).
    if (isBackendOnline === null) {
        return null;
    }

    return (
        <BackendStatusContext.Provider value={value}>
            {children}
        </BackendStatusContext.Provider>
    );
};
